import { useGetSingleSensitiveDataQuery } from "apis/sensitiveData";
import Async from "components/DesignSystem/AsyncComponents/Async";
import { Button } from "components/DesignSystem/Button/Button";
import { InfoItem } from "components/DesignSystem/InfoItem/InfoItem";
import Slider from "components/DesignSystem/Slider/Slider";
import { Tag } from "components/DesignSystem/Tag/Tag";
import { DeleteSensitiveDataModal } from "components/SensitiveData/DeleteSensitiveDataModal";
import { EditSensitiveDataDynamicForm } from "components/SensitiveData/SensitiveData";
import { SensitiveDataDetailField } from "components/SensitiveData/SensitiveDataDetailField";
import { SensitiveDataErrorState } from "components/SensitiveData/SensitiveDataErrorState";
import { SensitiveDataExternalDetails } from "components/SensitiveData/SensitiveDataExternalDetails";
import { SensitiveDataLinkedSection } from "components/SensitiveData/SensitiveDataLinkedSection";
import { SensitiveDataSection } from "components/SensitiveData/SensitiveDataSection";
import { useModal } from "hooks/useModal";
import { useParams } from "react-router-dom";
import { getSensitiveDataSectionTitle } from "util/getSensitiveDataSectionTitle";
import { isSensitiveDataFieldVisible } from "util/isSensitiveDataFieldVisible";
import { formatDate } from "utils/formatDate";

const SLIDER_WIDTH = 480;

export const SensitiveDataSlider = ({ onClose }: { onClose: () => void }) => {
  const editModal = useModal();
  const deleteModal = useModal();

  const { sensitiveDataId, entityId } = useParams<{
    sensitiveDataId: string;
    entityId: string;
  }>();

  const { data, isLoading, isSuccess, isError, isUninitialized, error } =
    useGetSingleSensitiveDataQuery(
      { entityId, formId: sensitiveDataId },
      { skip: !entityId || !sensitiveDataId }
    );

  const {
    sensitive_data_type,
    sensitive_data_type_id,
    country_state,
    form_data = [],
    has_edit_access,
    has_delete_access,
    linked_object,
    updated_at,
    updated_by,
  } = data || {};

  const valueByFieldKey = Object.fromEntries(
    form_data.map(({ field_key, value }) => [field_key, value])
  );

  const visibleFields = form_data.filter((field) =>
    isSensitiveDataFieldVisible(field, valueByFieldKey)
  );

  const sectionTitle = getSensitiveDataSectionTitle(
    sensitive_data_type_id,
    sensitive_data_type
  );

  const handleDeleted = () => {
    deleteModal.close();
    onClose();
  };

  return (
    <>
      <Slider.Root open onOpenChange={onClose}>
        <Slider.Content open useCustomOverlay width={SLIDER_WIDTH}>
          <Async.Root
            {...{
              isLoading: isLoading || isUninitialized,
              isSuccess,
              isError,
              isEmpty: false,
            }}
          >
            <Async.ErrorHandler>
              <SensitiveDataErrorState error={error} />
            </Async.ErrorHandler>
            <Async.Success>
              <Slider.Header>
                <Slider.Title>
                  <span className="t-flex t-items-center t-gap-1.5">
                    {sensitive_data_type}
                    {country_state && (
                      <Tag tagType="purple" size="small" icon={false} rounded>
                        {country_state}
                      </Tag>
                    )}
                  </span>
                </Slider.Title>
                <Slider.Close />
              </Slider.Header>

              <Slider.Body className="t-flex t-flex-col t-gap-4 t-bg-surface t-px-2 t-py-5">
                <SensitiveDataSection title={sectionTitle}>
                  <div className="t-grid t-grid-cols-2 t-gap-4">
                    {Boolean(country_state) && (
                      <InfoItem
                        label="State"
                        valueClassName="t-text-body"
                        className="t-min-w-0"
                      >
                        {country_state}
                      </InfoItem>
                    )}

                    {visibleFields.map((field) => (
                      <SensitiveDataDetailField
                        field={field}
                        formId={sensitiveDataId}
                        key={field.uuid}
                      />
                    ))}

                    {Boolean(updated_at) && (
                      <InfoItem
                        label="Last updated on"
                        valueClassName="t-text-body"
                        className="t-min-w-0"
                      >
                        {formatDate(updated_at!)}
                      </InfoItem>
                    )}

                    {Boolean(updated_by) && (
                      <InfoItem
                        label="Last updated by"
                        valueClassName="t-text-body"
                        className="t-min-w-0"
                      >
                        {updated_by}
                      </InfoItem>
                    )}
                  </div>
                </SensitiveDataSection>

                <SensitiveDataLinkedSection linkedObject={linked_object} />

                <SensitiveDataExternalDetails
                  sensitiveDataId={sensitiveDataId}
                />
              </Slider.Body>

              <Slider.Footer>
                <div className="t-grid t-gap-4 t-grid-cols-2">
                  {has_edit_access && (
                    <Button
                      type="button"
                      customType="secondary"
                      block
                      onClick={editModal.open}
                    >
                      Edit details
                    </Button>
                  )}
                  {has_delete_access && (
                    <Button
                      type="button"
                      customType="danger_outlined"
                      block
                      onClick={deleteModal.open}
                    >
                      Delete
                    </Button>
                  )}
                </div>
              </Slider.Footer>
            </Async.Success>
          </Async.Root>
        </Slider.Content>
      </Slider.Root>

      {editModal.isOpen && (
        <EditSensitiveDataDynamicForm
          close={editModal.close}
          isOpen={editModal.isOpen}
          formId={sensitiveDataId}
        />
      )}

      <DeleteSensitiveDataModal
        isOpen={deleteModal.isOpen}
        close={deleteModal.close}
        sensitiveDataId={sensitiveDataId}
        onDeleted={handleDeleted}
      />
    </>
  );
};
